import type { CSSProperties } from "react";
import { cx } from "../cx";
import styles from "./Avatar.module.css";

// ui-lib avatar initials palette, in Clockify's order.
const PALETTE = [
  "--ui-avatar-pink",
  "--ui-avatar-purple",
  "--ui-avatar-indigo",
  "--ui-avatar-blue",
  "--ui-avatar-cyan",
  "--ui-avatar-teal",
  "--ui-avatar-light-green",
  "--ui-avatar-deep-orange",
  "--ui-avatar-brown",
] as const;

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Stable per seed (user id or name), so a person keeps their colour everywhere. */
export function avatarColorVar(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `var(${PALETTE[h % PALETTE.length]})`;
}

export interface AvatarProps {
  name: string;
  /** Seed for the colour; defaults to the name. */
  colorSeed?: string;
  src?: string | null;
  size?: number;
  shape?: "circle" | "square";
  className?: string;
}

export function Avatar({ name, colorSeed, src, size = 32, shape = "circle", className }: AvatarProps) {
  const style = {
    width: size,
    height: size,
    fontSize: Math.round(size / 2.2),
    background: src ? undefined : avatarColorVar(colorSeed ?? name),
  } as CSSProperties;
  return (
    <span className={cx(styles.avatar, shape === "square" && styles.square, className)} style={style} title={name}>
      {src ? <img src={src} alt={name} className={styles.img} /> : <span aria-hidden="true">{initialsOf(name)}</span>}
      {!src && <span className={styles.srOnly}>{name}</span>}
    </span>
  );
}
